import { useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal from 'components/shared/StandardModal';
import { DischargeChecklistItem } from 'models/DischargeChecklistItem';
import {
  createDischargeChecklistItem,
  dischargeChecklistQueryKeys,
  updateDischargeChecklistItem,
} from 'services/api/dischargeChecklists';
import { validationErrorMessage } from 'services/api/validationErrors';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

export const LABEL_MAX_LENGTH = 120;

type Props = {
  // Absent means Add; present means Edit.
  item?: DischargeChecklistItem;
  groupTypeId: string;
  clientId?: string;
  onClose: () => void;
};

export default function DischargeChecklistItemModal({ item, groupTypeId, clientId, onClose }: Props) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const [label, setLabel] = useState(item?.label ?? '');
  const [error, setError] = useState<string | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      item
        ? updateDischargeChecklistItem({ id: item.id, clientId, label: label.trim() })
        : createDischargeChecklistItem({ clientId, groupTypeId, label: label.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dischargeChecklistQueryKeys.base });
      addToast({ text: 'Checklist item saved' });
      onClose();
    },
    onError: (e) => setError(validationErrorMessage(e) ?? 'Something went wrong. Please try again.'),
  });

  // An unchanged label isn't a rename, so it can't move "Last Updated".
  const canSubmit = label.trim().length > 0 && label.trim() !== item?.label && !isPending;

  return (
    <StandardModal title={item ? 'Edit Checklist Item' : 'Add Checklist Item'} onCancel={onClose}>
      <InputGroup title='Item Label'>
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder='Item Label'
          maxLength={LABEL_MAX_LENGTH}
          disabled={isPending}
          autoFocus
          data-testid='checklist-item-label'
        />
        <Hint data-testid='checklist-item-remaining'>Remaining Characters: {LABEL_MAX_LENGTH - label.length}</Hint>
      </InputGroup>

      {error && <ErrorText>{error}</ErrorText>}

      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={() => mutate()} loading={isPending} disabled={!canSubmit} data-testid='checklist-item-submit'>
          Submit
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Hint = styled(BodySmall)`
  display: block;
  margin-top: 6px;
  color: ${colors.black75};
`;

const ErrorText = styled(BodySmall)`
  display: block;
  margin: -12px 0 24px;
  color: ${colors.red100};
`;
